"use client";

import React from "react";
import { usePathname } from "next/navigation";
import { sideBarData } from "./app-sidebar";
import {
  findActiveNavMatch,
  getNavItemByUrl,
  isNavItemActive,
} from "./sidebar/find-active-nav-item";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "./ui/breadcrumb";
import { useBreadcrumb } from "@/contexts/breadcrumb-context";
import { Skeleton } from "./ui/skeleton";
import { cn } from "@/utils/utils";
import type { NavMainItem } from "./sidebar/nav-types";

const allSections = sideBarData.sections;

const BreadcrumbLabel = ({
  label,
  href,
  icon,
  className,
  isActive = false,
}: {
  label: React.ReactNode;
  href?: string;
  icon?: NavMainItem["icon"];
  className?: string;
  isActive?: boolean;
}) => {
  const Icon =
    icon ?? (href ? getNavItemByUrl(href, allSections)?.icon : undefined);

  return (
    <span
      className={cn(
        "flex min-w-0 items-center gap-1.5 text-sm font-medium",
        isActive &&
          "w-full overflow-hidden text-foreground [&_svg]:text-foreground",
        className,
      )}
    >
      {Icon && <Icon className="size-3.5 shrink-0" />}
      <span className={cn(isActive && "min-w-0 flex-1 truncate")}>{label}</span>
    </span>
  );
};

const CustomBreadcrumbItems = ({
  items,
  isPending,
}: {
  items: ReturnType<typeof useBreadcrumb>["breadcrumbs"];
  isPending: boolean;
}) => (
  <>
    {items.map((item, index) => {
      const isActive = !isPending && index === items.length - 1;

      return (
        <React.Fragment key={index}>
          {index > 0 && (
            <BreadcrumbSeparator className="hidden shrink-0 md:block" />
          )}
          <BreadcrumbItem
            className={cn(
              isActive ? "min-w-0 flex-1 overflow-hidden" : "shrink-0",
            )}
          >
            {item.href && !isActive ? (
              <BreadcrumbLink href={item.href} onClick={item.onClick}>
                {typeof item.label === "string" ? (
                  <BreadcrumbLabel label={item.label} href={item.href} />
                ) : (
                  item.label
                )}
              </BreadcrumbLink>
            ) : (
              <span className="block min-w-0 overflow-hidden">
                {typeof item.label === "string" ? (
                  <BreadcrumbLabel
                    label={item.label}
                    href={item.href}
                    isActive={isActive}
                  />
                ) : (
                  item.label
                )}
              </span>
            )}
          </BreadcrumbItem>
        </React.Fragment>
      );
    })}
    {isPending && (
      <>
        {items.length > 0 && (
          <BreadcrumbSeparator className="hidden shrink-0 md:block" />
        )}
        <BreadcrumbItem className="min-w-0 flex-1 overflow-hidden">
          <Skeleton className="h-4 w-32 max-w-full rounded-sm" />
        </BreadcrumbItem>
      </>
    )}
  </>
);

export const HeaderBreadcrumbs = () => {
  const pathname = usePathname();
  const { breadcrumbs, isPending } = useBreadcrumb();
  const hasCustomBreadcrumbs = breadcrumbs.length > 0 || isPending;

  const generateBreadcrumbs = (path: string, basePath?: string) => {
    const pathParts = path
      .replace(basePath || "", "")
      .split("/")
      .filter(Boolean);
    const generatedBreadcrumbs: React.ReactNode[] = [];
    let accumulatedPath = "";

    pathParts.forEach((part, index) => {
      accumulatedPath = `/${pathParts.slice(0, index + 1).join("/")}`;
      const fullPath = `${basePath}${accumulatedPath}`;
      const partTitle = getNavItemByUrl(fullPath, allSections)?.title ?? "";
      const partIcon = getNavItemByUrl(fullPath, allSections)?.icon;
      const isActive = index === pathParts.length - 1;

      generatedBreadcrumbs.push(
        <div
          key={`${part}-${index}`}
          className="flex min-w-0 items-center gap-2"
        >
          <BreadcrumbSeparator className="hidden shrink-0 md:block" />
          <BreadcrumbItem
            className={cn(
              isActive ? "min-w-0 flex-1 overflow-hidden" : "shrink-0",
            )}
          >
            {isActive ? (
              <span className="capitalize">
                <BreadcrumbLabel
                  label={partTitle || part}
                  href={fullPath}
                  icon={partIcon}
                  isActive
                />
              </span>
            ) : (
              <BreadcrumbLink className="capitalize" href={fullPath}>
                <BreadcrumbLabel
                  label={partTitle || part}
                  href={fullPath}
                  icon={partIcon}
                />
              </BreadcrumbLink>
            )}
          </BreadcrumbItem>
        </div>,
      );
    });

    return generatedBreadcrumbs;
  };

  const matchedNavItem = findActiveNavMatch(pathname, allSections);

  const currentSidebarSubItems = sideBarData.sections
    .flatMap((section) => section.items)
    .find((item) => item.url === matchedNavItem?.url)
    ?.items?.find((item) => isNavItemActive(pathname, item.url));

  const currentSidebarItem = matchedNavItem;

  const hasGeneratedBreadcrumbs =
    Boolean(currentSidebarItem?.url) && pathname.split("/").length > 2;
  const isNavItemActiveCrumb =
    Boolean(currentSidebarItem) &&
    pathname === currentSidebarItem?.url &&
    !currentSidebarSubItems &&
    !hasGeneratedBreadcrumbs;

  return (
    <Breadcrumb className="min-w-0 flex-1">
      <BreadcrumbList className="min-w-0 flex-1 flex-nowrap overflow-hidden break-normal">
        {hasCustomBreadcrumbs ? (
          <CustomBreadcrumbItems items={breadcrumbs} isPending={isPending} />
        ) : (
          <>
            {currentSidebarItem && (
              <BreadcrumbItem
                className={cn(
                  !isNavItemActiveCrumb && "hidden md:block",
                  isNavItemActiveCrumb && "min-w-0 flex-1 overflow-hidden",
                )}
              >
                {isNavItemActiveCrumb ? (
                  <span>
                    <BreadcrumbLabel
                      label={currentSidebarItem.title}
                      href={currentSidebarItem.url}
                      icon={currentSidebarItem.icon}
                      isActive
                    />
                  </span>
                ) : (
                  <BreadcrumbLink href={currentSidebarItem.url}>
                    <BreadcrumbLabel
                      label={currentSidebarItem.title}
                      href={currentSidebarItem.url}
                      icon={currentSidebarItem.icon}
                    />
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
            )}
            {currentSidebarSubItems && (
              <>
                <BreadcrumbSeparator
                  className={cn(
                    "hidden shrink-0",
                    !isNavItemActiveCrumb && "md:block",
                  )}
                />
                <BreadcrumbItem
                  className={cn(
                    isNavItemActiveCrumb && "min-w-0 flex-1 overflow-hidden",
                  )}
                >
                  {!hasGeneratedBreadcrumbs ? (
                    <span>
                      <BreadcrumbLabel
                        label={currentSidebarSubItems.title}
                        href={currentSidebarSubItems.url}
                        icon={currentSidebarSubItems.icon}
                        isActive
                      />
                    </span>
                  ) : (
                    <BreadcrumbLink href={currentSidebarSubItems.url}>
                      <BreadcrumbLabel
                        label={currentSidebarSubItems.title}
                        href={currentSidebarSubItems.url}
                        icon={currentSidebarSubItems.icon}
                      />
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </>
            )}
            {pathname.split("/").length > 2 &&
              generateBreadcrumbs(pathname, currentSidebarItem?.url)}
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
